import { kindClock, kindSide, MAX_PLAN_CHAPTERS, MAX_PLAN_OPPONENTS, MAX_PLAN_PLAYERS, PACES, planClock, ROLES, WEAPONS, type Area, type ExecutionKind, type Pace, type Plan, type PlanAction, type Role, type Weapon } from "@69ly/shared";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCallouts } from "../../lib/callouts.ts";
import { addAction, endPoint, isPlayer, ownerOf, parsePlanClock, planId, removeAction, removeActor, shiftAction, updateAction, withActor, type PlanActor } from "../../lib/plan.ts";
import { ComboGlyphs } from "../ComboBits.tsx";
import { GrenadeGlyph } from "../GrenadeGlyph.tsx";
import { Icon } from "../Icon.tsx";
import { ActionLinks, ActionText, actionIcon, ActorHead, warningText, type PartsContext } from "./PlanParts.tsx";

export type BuildMode = "move" | "place-player" | "place-opponent" | "bomb";

export interface Doc {
  title: string;
  kind: ExecutionKind;
  target: Area;
  notes: string;
  plan: Plan;
}

export interface BuilderCtx extends PartsContext {
  doc: Doc;
  people: { id: string; name: string }[];
  changeDoc: (fn: (d: Doc) => Doc) => void;
  selected: string | null;
  select: (id: string | null) => void;
  selectedAction: string | null;
  selectAction: (id: string | null) => void;
  mode: BuildMode;
  setMode: (m: BuildMode) => void;
  pace: Pace;
  setPace: (p: Pace) => void;
  showTargets: boolean;
  setShowTargets: (v: boolean) => void;
  /** The playhead, for "add a chapter here". */
  elapsed: number;
}

/** A time on the clock, typed as 1:12 (tenths allowed), kept when it reads. */
export function ClockField({ value, onChange, label, max, className }: { value: number; onChange: (v: number) => void; label: string; max: number; className?: string }) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? planClock(value);
  const parsed = text === null ? value : parsePlanClock(text);
  const bad = parsed === null || parsed > max;
  return (
    <input
      className={`clock-field mono${bad ? " is-invalid" : ""}${className ? ` ${className}` : ""}`}
      value={shown}
      aria-label={label}
      aria-invalid={bad || undefined}
      inputMode="decimal"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (!bad && parsed !== null) onChange(parsed);
        setText(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setText(null);
      }}
    />
  );
}

/** An action in plain words, for lists of choices (no links, no icons). */
function useActionLabel(ctx: PartsContext) {
  const { t } = useTranslation();
  const callouts = useCallouts();
  return (a: PlanAction): string => {
    switch (a.kind) {
      case "move": {
        const place = callouts.at(ctx.map, a.to.x, a.to.y, a.to.z ?? null);
        return place ? t(`plan.moveTo.${a.pace}`, { place }) : t(`plan.move.${a.pace}`);
      }
      case "hold":
        return t("plan.holdFor", { seconds: a.seconds });
      case "call":
        return `“${a.text}”`;
      case "throw":
        return ctx.stuffs.find((s) => s.id === a.stuffId)?.title ?? t("plan.missingThrow");
      case "combo":
        return ctx.combos.find((c) => c.id === a.comboId)?.title ?? t("plan.missingCombo");
    }
  };
}

/** When an action starts: right after the one before, at a time, or tied to another action. */
function TimingEditor({ a, ctx }: { a: PlanAction; ctx: BuilderCtx }) {
  const { t } = useTranslation();
  const label = useActionLabel(ctx);
  const mode = a.at !== null ? "at" : a.after ? "after" : "auto";
  const time = ctx.schedule.times.get(a.id);
  const set = (fn: (x: PlanAction) => PlanAction) => ctx.changeDoc((d) => ({ ...d, plan: updateAction(d.plan, a.id, fn) }));
  const others = ([...ctx.plan.players, ...ctx.plan.opponents] as PlanActor[]).flatMap((actor) =>
    (actor.actions as PlanAction[]).filter((x) => x.id !== a.id).map((x) => ({ actor, x })),
  );
  const max = kindClock(ctx.doc.kind);
  return (
    <div className="plan-timing">
      <div className="segmented segmented-small" role="group" aria-label={t("plan.timing")}>
        <button type="button" className="seg" aria-pressed={mode === "auto"} onClick={() => set((x) => ({ ...x, at: null, after: null }))} title={t("plan.timingAutoHelp")}>
          {t("plan.timingAuto")}
        </button>
        <button type="button" className="seg" aria-pressed={mode === "at"} onClick={() => set((x) => ({ ...x, after: null, at: Math.round(ctx.schedule.toClock(time?.start ?? 0) * 10) / 10 }))}>
          {t("plan.timingAt")}
        </button>
        <button
          type="button"
          className="seg"
          aria-pressed={mode === "after"}
          disabled={others.length === 0}
          onClick={() => others[0] && set((x) => ({ ...x, at: null, after: { ref: others[0].x.id, event: "end", delay: 0 } }))}
        >
          {t("plan.timingAfter")}
        </button>
      </div>
      {mode === "at" && a.at !== null && (
        <label className="field-inline small">
          {t("plan.startsAt")}
          <ClockField value={a.at} max={max} label={t("plan.startsAt")} onChange={(v) => set((x) => ({ ...x, at: v }))} />
        </label>
      )}
      {mode === "after" && a.after && (
        <div className="plan-after">
          <select aria-label={t("plan.afterWhich")} value={a.after.ref} onChange={(e) => set((x) => ({ ...x, after: { ...x.after!, ref: e.target.value } }))}>
            {others.map(({ actor, x }) => (
              <option key={x.id} value={x.id}>
                {isPlayer(actor) ? `${actor.slot} · ${t(`role.${actor.role}`)}` : actor.label || t("plan.opponent")} — {label(x)}
              </option>
            ))}
          </select>
          <select aria-label={t("plan.afterEvent")} value={a.after.event} onChange={(e) => set((x) => ({ ...x, after: { ...x.after!, event: e.target.value as "start" | "end" | "lands" } }))}>
            {(["start", "end", "lands"] as const).map((ev) => (
              <option key={ev} value={ev}>
                {t(`plan.event.${ev}`)}
              </option>
            ))}
          </select>
          <label className="field-inline small">
            {t("plan.delay")}
            <input
              type="number"
              step={0.5}
              min={-30}
              max={60}
              className="plan-num"
              value={a.after.delay}
              onChange={(e) => set((x) => ({ ...x, after: { ...x.after!, delay: Number(e.target.value) || 0 } }))}
            />
            s
          </label>
        </div>
      )}
    </div>
  );
}

/** What can be changed on one action, besides when. */
function ActionEditor({ a, ctx }: { a: PlanAction; ctx: BuilderCtx }) {
  const { t } = useTranslation();
  const set = (fn: (x: PlanAction) => PlanAction) => ctx.changeDoc((d) => ({ ...d, plan: updateAction(d.plan, a.id, fn) }));
  return (
    <div className="plan-action-edit">
      <TimingEditor a={a} ctx={ctx} />
      {a.kind === "move" && (
        <div className="segmented segmented-small" role="group" aria-label={t("plan.pace")}>
          {PACES.map((p) => (
            <button key={p} type="button" className="seg" aria-pressed={a.pace === p} onClick={() => set((x) => (x.kind === "move" ? { ...x, pace: p } : x))}>
              {t(`plan.paces.${p}`)}
            </button>
          ))}
        </div>
      )}
      {a.kind === "hold" && (
        <>
          <label className="field-inline small">
            {t("plan.holdSeconds")}
            <input type="number" className="plan-num" min={0.5} max={90} step={0.5} value={a.seconds} onChange={(e) => set((x) => (x.kind === "hold" ? { ...x, seconds: Math.min(90, Math.max(0.5, Number(e.target.value) || 0.5)) } : x))} />
            s
          </label>
          <p className="muted small">{t("plan.holdAimHelp")}</p>
        </>
      )}
      {(a.kind === "throw" || a.kind === "combo") && (
        <label className="field-inline small">
          {t("plan.lineup")}
          <input
            type="number"
            className="plan-num"
            min={0}
            max={10}
            step={0.5}
            placeholder={t("plan.lineupAuto")}
            value={a.lineup ?? ""}
            onChange={(e) => set((x) => (x.kind === "throw" || x.kind === "combo" ? { ...x, lineup: e.target.value === "" ? null : Math.min(10, Math.max(0, Number(e.target.value))) } : x))}
          />
          s
        </label>
      )}
      {a.kind === "combo" && (
        <label className="field-inline small">
          {t("plan.comboGap")}
          <input type="number" className="plan-num" min={0.3} max={10} step={0.1} value={a.gap} onChange={(e) => set((x) => (x.kind === "combo" ? { ...x, gap: Math.min(10, Math.max(0.3, Number(e.target.value) || 1.2)) } : x))} />
          s
        </label>
      )}
      {a.kind === "call" && (
        <input className="plan-call-input" value={a.text} maxLength={80} aria-label={t("plan.callText")} onChange={(e) => set((x) => (x.kind === "call" ? { ...x, text: e.target.value } : x))} />
      )}
    </div>
  );
}

/** Singles or combos to add, the closest to where the player is first; a search narrows them. */
function Picker({ kind, actor, ctx, onClose }: { kind: "throw" | "combo"; actor: PlanActor; ctx: BuilderCtx; onClose: () => void }) {
  const { t } = useTranslation();
  const callouts = useCallouts();
  const [query, setQuery] = useState("");
  const side = kindSide(ctx.doc.kind);
  const from = endPoint(actor, ctx.lookup, ctx.selectedAction ? (actor.actions as PlanAction[]).findIndex((x) => x.id === ctx.selectedAction) + 1 || undefined : undefined);
  const q = query.trim().toLowerCase();
  const add = (action: PlanAction) => {
    ctx.changeDoc((d) => ({ ...d, plan: addAction(d.plan, actor.id, action, ctx.selectedAction && ownerOf(d.plan, ctx.selectedAction)?.id === actor.id ? ctx.selectedAction : null) }));
    ctx.selectAction(action.id);
    onClose();
  };
  const singles = useMemo(
    () =>
      ctx.stuffs
        .filter((s) => s.side === side && (!q || s.title.toLowerCase().includes(q)))
        .map((s) => ({ s, d: Math.hypot(s.throw.x - from.x, s.throw.y - from.y) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 40),
    [ctx.stuffs, side, q, from.x, from.y],
  );
  const combos = useMemo(
    () =>
      ctx.combos
        .filter((c) => c.side === side && c.steps.length > 0 && (!q || c.title.toLowerCase().includes(q)))
        .map((c) => ({ c, d: Math.hypot(c.steps[0].stuff.throw.x - from.x, c.steps[0].stuff.throw.y - from.y) }))
        .sort((a, b) => a.d - b.d),
    [ctx.combos, side, q, from.x, from.y],
  );
  return (
    <div className="plan-picker" role="dialog" aria-label={kind === "throw" ? t("plan.pickThrow") : t("plan.pickCombo")}>
      <div className="plan-picker-head">
        <input autoFocus value={query} placeholder={t("plan.search")} aria-label={t("plan.search")} onChange={(e) => setQuery(e.target.value)} />
        <button type="button" className="btn btn-quiet btn-icon" aria-label={t("common.cancel")} onClick={onClose}>
          <Icon name="close" size={14} />
        </button>
      </div>
      <p className="muted small">{t("plan.pickerHelp")}</p>
      <ul className="plan-picker-list">
        {kind === "throw"
          ? singles.map(({ s, d }) => (
              <li key={s.id}>
                <button type="button" onClick={() => add({ id: planId("a"), kind: "throw", stuffId: s.id, lineup: null, at: null, after: null })}>
                  <GrenadeGlyph grenade={s.grenade} size={12} />
                  <span className="plan-picker-title">{s.title}</span>
                  <span className="muted small">
                    {callouts.at(ctx.map, s.throw.x, s.throw.y, s.throw.z) ?? ""} · {Math.round(d / 32)} m
                  </span>
                </button>
              </li>
            ))
          : combos.map(({ c, d }) => (
              <li key={c.id}>
                <button type="button" onClick={() => add({ id: planId("a"), kind: "combo", comboId: c.id, lineup: null, gap: 1.2, at: null, after: null })}>
                  <ComboGlyphs combo={c} size={9} />
                  <span className="plan-picker-title">{c.title}</span>
                  <span className="muted small">{Math.round(d / 32)} m</span>
                </button>
              </li>
            ))}
        {(kind === "throw" ? singles.length : combos.length) === 0 && <li className="muted small">{t("plan.pickerEmpty")}</li>}
      </ul>
    </div>
  );
}

/** One actor's actions: each with its time, the selected one open to change. */
function ActionList({ actor, ctx }: { actor: PlanActor; ctx: BuilderCtx }) {
  const { t } = useTranslation();
  const list = actor.actions as PlanAction[];
  if (list.length === 0) return <p className="muted small">{t("plan.noActions")}</p>;
  return (
    <ol className="plan-lines is-editing">
      {list.map((x, i) => {
        const time = ctx.schedule.times.get(x.id);
        const warn = ctx.schedule.warnings.find((w) => w.action === x.id);
        const open = ctx.selectedAction === x.id;
        return (
          <li key={x.id} className={`plan-line${open ? " is-open" : ""}${warn ? " has-warning" : ""}`}>
            <div className="plan-line-row">
              <button type="button" className="plan-line-button" aria-expanded={open} onClick={() => ctx.selectAction(open ? null : x.id)}>
                <span className="plan-line-time mono">
                  {time ? `${planClock(ctx.schedule.toClock(time.start))}` : "–"}
                  {x.at !== null && <Icon name="lock" size={10} />}
                </span>
                <Icon name={actionIcon(x)} size={13} />
                <ActionText a={x} ctx={ctx} />
                {x.after && <Icon name="link" size={12} />}
              </button>
              <ActionLinks a={x} ctx={ctx} />
              <span className="plan-line-tools">
                <button type="button" className="btn btn-quiet btn-icon btn-tiny" aria-label={t("plan.earlier")} disabled={i === 0} onClick={() => ctx.changeDoc((d) => ({ ...d, plan: shiftAction(d.plan, x.id, -1) }))}>
                  <Icon name="chevronUp" size={12} />
                </button>
                <button type="button" className="btn btn-quiet btn-icon btn-tiny" aria-label={t("plan.later")} disabled={i === list.length - 1} onClick={() => ctx.changeDoc((d) => ({ ...d, plan: shiftAction(d.plan, x.id, 1) }))}>
                  <Icon name="chevronDown" size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-quiet btn-icon btn-tiny btn-danger"
                  aria-label={t("plan.removeAction")}
                  onClick={() => {
                    ctx.changeDoc((d) => ({ ...d, plan: removeAction(d.plan, x.id) }));
                    if (open) ctx.selectAction(null);
                  }}
                >
                  <Icon name="trash" size={12} />
                </button>
              </span>
            </div>
            {warn && <p className="plan-warn small">{warningText(warn, t)}</p>}
            {open && time && (
              <p className="muted small plan-line-span">
                {planClock(ctx.schedule.toClock(time.start))} → {planClock(ctx.schedule.toClock(time.end))}
              </p>
            )}
            {open && <ActionEditor a={x} ctx={ctx} />}
          </li>
        );
      })}
    </ol>
  );
}

/** Adding to the selected actor: moves by clicking the map, the rest with these buttons. */
function AddBar({ actor, ctx, withThrows }: { actor: PlanActor; ctx: BuilderCtx; withThrows: boolean }) {
  const { t } = useTranslation();
  const [picker, setPicker] = useState<"throw" | "combo" | null>(null);
  const after = ctx.selectedAction && (actor.actions as PlanAction[]).some((x) => x.id === ctx.selectedAction) ? ctx.selectedAction : null;
  const add = (action: PlanAction) => {
    ctx.changeDoc((d) => ({ ...d, plan: addAction(d.plan, actor.id, action, after) }));
    ctx.selectAction(action.id);
  };
  const s = ctx.schedule.actors.get(actor.id);
  const lastYaw = s?.segments.length ? s.segments[s.segments.length - 1].yawTo : actor.start.yaw;
  return (
    <div className="plan-add">
      <p className="small plan-add-hint">
        <Icon name="route" size={13} /> {t("plan.clickToMove")}
      </p>
      <div className="segmented segmented-small" role="group" aria-label={t("plan.newMovePace")}>
        {PACES.map((p) => (
          <button key={p} type="button" className="seg" aria-pressed={ctx.pace === p} onClick={() => ctx.setPace(p)}>
            {t(`plan.paces.${p}`)}
          </button>
        ))}
      </div>
      <div className="row plan-add-buttons">
        <button type="button" className="btn btn-secondary btn-small" onClick={() => add({ id: planId("a"), kind: "hold", yaw: Math.round(lastYaw), seconds: 3, at: null, after: null })}>
          <Icon name="hold" size={13} />
          {t("plan.addHold")}
        </button>
        {withThrows && (
          <>
            <button type="button" className="btn btn-secondary btn-small" aria-pressed={picker === "throw"} onClick={() => setPicker(picker === "throw" ? null : "throw")}>
              <Icon name="target" size={13} />
              {t("plan.addThrow")}
            </button>
            <button type="button" className="btn btn-secondary btn-small" aria-pressed={picker === "combo"} onClick={() => setPicker(picker === "combo" ? null : "combo")}>
              <Icon name="target" size={13} />
              {t("plan.addCombo")}
            </button>
          </>
        )}
        <button type="button" className="btn btn-secondary btn-small" onClick={() => add({ id: planId("a"), kind: "call", text: t("plan.defaultCall"), at: null, after: null })}>
          <Icon name="chat" size={13} />
          {t("plan.addCall")}
        </button>
      </div>
      {withThrows && (
        <label className="check small">
          <input type="checkbox" checked={ctx.showTargets} onChange={(e) => ctx.setShowTargets(e.target.checked)} />
          {t("plan.showTargets")}
        </label>
      )}
      {picker && <Picker kind={picker} actor={actor} ctx={ctx} onClose={() => setPicker(null)} />}
    </div>
  );
}

function PlayerSettings({ actor, ctx }: { actor: PlanActor; ctx: BuilderCtx }) {
  const { t } = useTranslation();
  const set = (fn: (a: PlanActor) => PlanActor) => ctx.changeDoc((d) => ({ ...d, plan: withActor(d.plan, actor.id, fn) }));
  return (
    <div className="plan-settings">
      {isPlayer(actor) ? (
        <>
          <label className="field-inline small">
            {t("plan.role")}
            <select value={actor.role} onChange={(e) => set((a) => ({ ...a, role: e.target.value as Role }) as PlanActor)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`role.${r}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="field-inline small">
            {t("plan.teammate")}
            <select value={actor.memberId ?? ""} onChange={(e) => set((a) => ({ ...a, memberId: e.target.value || null }) as PlanActor)}>
              <option value="">{t("plan.anyone")}</option>
              {ctx.people.map((p) => (
                <option key={p.id} value={p.id} disabled={ctx.plan.players.some((x) => x.id !== actor.id && x.memberId === p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <label className="field-inline small">
          {t("plan.opponentLabel")}
          <input value={actor.label} maxLength={30} placeholder={t("plan.opponentPlaceholder")} onChange={(e) => set((a) => ({ ...a, label: e.target.value }) as PlanActor)} />
        </label>
      )}
      <label className="field-inline small">
        {t("plan.weaponLabel")}
        <select value={actor.weapon} onChange={(e) => set((a) => ({ ...a, weapon: e.target.value as Weapon }) as PlanActor)}>
          {WEAPONS.map((w) => (
            <option key={w} value={w}>
              {t(`plan.weapon.${w}`)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="btn btn-quiet btn-small btn-danger"
        onClick={() => {
          ctx.changeDoc((d) => ({ ...d, plan: removeActor(d.plan, actor.id) }));
          ctx.select(null);
        }}
      >
        <Icon name="trash" size={13} />
        {isPlayer(actor) ? t("plan.removePlayer") : t("plan.removeOpponent")}
      </button>
    </div>
  );
}

/** The players (or opponents) list, the selected one open. */
function ActorsTab({ ctx, enemies }: { ctx: BuilderCtx; enemies: boolean }) {
  const { t } = useTranslation();
  const list: PlanActor[] = enemies ? ctx.plan.opponents : ctx.plan.players;
  const max = enemies ? MAX_PLAN_OPPONENTS : MAX_PLAN_PLAYERS;
  const placing = ctx.mode === (enemies ? "place-opponent" : "place-player");
  return (
    <div className="plan-actors-tab">
      <ul className="plan-actor-picks">
        {list.map((a) => (
          <li key={a.id}>
            <button type="button" className="plan-actor-pick" aria-pressed={ctx.selected === a.id} onClick={() => ctx.select(ctx.selected === a.id ? null : a.id)}>
              <ActorHead a={a} ctx={ctx} />
              <span className="muted small">{t("plan.actionsCount", { count: a.actions.length })}</span>
            </button>
          </li>
        ))}
      </ul>
      {list.length < max && (
        <button type="button" className={`btn ${placing ? "btn-primary" : "btn-secondary"} btn-small`} aria-pressed={placing} onClick={() => ctx.setMode(placing ? "move" : enemies ? "place-opponent" : "place-player")}>
          <Icon name="plus" size={14} />
          {placing ? t("plan.clickToPlace") : enemies ? t("plan.addOpponent") : t("plan.addPlayer")}
        </button>
      )}
      {list
        .filter((a) => a.id === ctx.selected)
        .map((a) => (
          <section key={a.id} className={`plan-actor-edit ${isPlayer(a) ? `slot-${a.slot}` : "is-enemy"}`} aria-label={t("plan.editing")}>
            <PlayerSettings actor={a} ctx={ctx} />
            <p className="muted small">{t("plan.startHelp")}</p>
            <ActionList actor={a} ctx={ctx} />
            <AddBar actor={a} ctx={ctx} withThrows={!enemies} />
          </section>
        ))}
      {!ctx.selected && list.length > 0 && <p className="muted small">{t("plan.pickToEdit")}</p>}
    </div>
  );
}

/** The plan as a whole: its window, chapters, the retake's bomb, notes. */
function PlanTab({ ctx }: { ctx: BuilderCtx }) {
  const { t } = useTranslation();
  const { doc } = ctx;
  const max = kindClock(doc.kind);
  const setPlan = (fn: (p: Plan) => Plan) => ctx.changeDoc((d) => ({ ...d, plan: fn(d.plan) }));
  const here = Math.round(ctx.schedule.toClock(ctx.elapsed) * 10) / 10;
  return (
    <div className="plan-plan-tab">
      <section>
        <h3 className="eyebrow">{t("plan.window")}</h3>
        <p className="muted small">{doc.kind === "retake" ? t("plan.windowRetakeHelp") : t("plan.windowHelp")}</p>
        <div className="row">
          <label className="field-inline small">
            {t("plan.from")}
            <ClockField value={doc.plan.window.from} max={max} label={t("plan.from")} onChange={(v) => setPlan((p) => ({ ...p, window: { ...p.window, from: v } }))} />
          </label>
          <label className="field-inline small">
            {t("plan.to")}
            <ClockField value={doc.plan.window.to} max={max} label={t("plan.to")} onChange={(v) => setPlan((p) => ({ ...p, window: { ...p.window, to: v } }))} />
          </label>
        </div>
        {doc.plan.window.from <= doc.plan.window.to && <p className="field-error small">{t("plan.windowBackwards")}</p>}
      </section>
      {doc.kind === "retake" && (
        <section>
          <h3 className="eyebrow">{t("plan.bomb")}</h3>
          <button type="button" className={`btn ${ctx.mode === "bomb" ? "btn-primary" : "btn-secondary"} btn-small`} onClick={() => ctx.setMode(ctx.mode === "bomb" ? "move" : "bomb")}>
            {ctx.mode === "bomb" ? t("plan.clickBomb") : doc.plan.bomb ? t("plan.moveBomb") : t("plan.placeBomb")}
          </button>
          <label className="check small">
            <input type="checkbox" checked={doc.plan.bombKnownAt === null} onChange={(e) => setPlan((p) => ({ ...p, bombKnownAt: e.target.checked ? null : Math.max(0, p.window.from - 5) }))} />
            {t("plan.bombKnownFromStart")}
          </label>
          {doc.plan.bombKnownAt !== null && (
            <label className="field-inline small">
              {t("plan.bombKnownWhen")}
              <ClockField value={doc.plan.bombKnownAt} max={max} label={t("plan.bombKnownWhen")} onChange={(v) => setPlan((p) => ({ ...p, bombKnownAt: v }))} />
            </label>
          )}
          <p className="muted small">{t("plan.bombHelp")}</p>
        </section>
      )}
      <section>
        <h3 className="eyebrow">{t("plan.chapters")}</h3>
        <p className="muted small">{t("plan.chaptersHelp")}</p>
        <ol className="plan-chapters">
          {[...doc.plan.chapters]
            .sort((a, b) => b.at - a.at)
            .map((c) => (
              <li key={c.id} className="row">
                <ClockField value={c.at} max={max} label={t("plan.chapterTime", { name: c.name })} onChange={(v) => setPlan((p) => ({ ...p, chapters: p.chapters.map((x) => (x.id === c.id ? { ...x, at: v } : x)) }))} />
                <input value={c.name} maxLength={30} aria-label={t("plan.chapterName")} onChange={(e) => setPlan((p) => ({ ...p, chapters: p.chapters.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)) }))} />
                <button type="button" className="btn btn-quiet btn-icon btn-tiny btn-danger" aria-label={t("plan.removeChapter", { name: c.name })} onClick={() => setPlan((p) => ({ ...p, chapters: p.chapters.filter((x) => x.id !== c.id) }))}>
                  <Icon name="trash" size={12} />
                </button>
              </li>
            ))}
        </ol>
        {doc.plan.chapters.length < MAX_PLAN_CHAPTERS && (
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setPlan((p) => ({ ...p, chapters: [...p.chapters, { id: planId("c"), name: t("plan.chapterN", { n: p.chapters.length + 1 }), at: here }] }))}>
            <Icon name="plus" size={14} />
            {t("plan.addChapterAt", { time: planClock(here) })}
          </button>
        )}
      </section>
      <label className="field">
        <span>{t("form.notes")}</span>
        <textarea rows={4} maxLength={1000} value={doc.notes} onChange={(e) => ctx.changeDoc((d) => ({ ...d, notes: e.target.value }))} />
      </label>
    </div>
  );
}

/** The builder's side: players, opponents and the plan, one tab each. */
export function BuilderRail({ ctx, tab, setTab, footer }: { ctx: BuilderCtx; tab: "players" | "opponents" | "plan"; setTab: (t: "players" | "opponents" | "plan") => void; footer?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="plan-builder-rail">
      <div className="segmented" role="tablist" aria-label={t("plan.builderTabs")}>
        {(["players", "opponents", "plan"] as const).map((k) => (
          <button key={k} type="button" role="tab" className="seg" aria-selected={tab === k} aria-pressed={tab === k} onClick={() => setTab(k)}>
            {t(`plan.tabs.${k}`)}
            {k === "players" && <span className="muted"> {ctx.plan.players.length}</span>}
            {k === "opponents" && <span className="muted"> {ctx.plan.opponents.length}</span>}
          </button>
        ))}
      </div>
      {tab === "players" && <ActorsTab ctx={ctx} enemies={false} />}
      {tab === "opponents" && <ActorsTab ctx={ctx} enemies />}
      {tab === "plan" && <PlanTab ctx={ctx} />}
      {footer}
    </div>
  );
}
